import { create } from "zustand";

export type ClusteringStage =
  | "idle"
  | "embedding"
  | "clustering"
  | "labeling"
  | "complete"
  | "error";

export interface ClusteringProgress {
  stage: ClusteringStage;
  statusMessage: string;
  percent: number;
}

interface ClusteringState {
  isClustering: boolean;
  isCompleted: boolean;
  error: string | null;
  jobId: string | null;
  progress: ClusteringProgress;

  startClustering: (jobId: string) => void;
  updateProgress: (update: Partial<ClusteringProgress>) => void;
  completeClustering: (message: string) => void;
  failClustering: (error: string) => void;
  resetClustering: () => void;
}

const initialProgress: ClusteringProgress = {
  stage: "idle",
  statusMessage: "",
  percent: 0,
};

const stagePercent: Record<ClusteringStage, number> = {
  idle: 0,
  embedding: 20,
  clustering: 50,
  labeling: 75,
  complete: 100,
  error: 0,
};

export const useClusteringStore = create<ClusteringState>((set, get) => ({
  isClustering: false,
  isCompleted: false,
  error: null,
  jobId: null,
  progress: { ...initialProgress },

  startClustering: (jobId) =>
    set({
      isClustering: true,
      isCompleted: false,
      error: null,
      jobId,
      progress: {
        stage: "idle",
        statusMessage: "Starting clustering...",
        percent: 5,
      },
    }),

  updateProgress: (update) =>
    set((state) => {
      const newProgress = { ...state.progress, ...update };
      // Auto-set percent from stage if not explicitly provided
      if (update.stage && !update.percent) {
        newProgress.percent = stagePercent[update.stage] ?? state.progress.percent;
      }
      return { progress: newProgress };
    }),

  completeClustering: (message) =>
    set({
      isClustering: false,
      isCompleted: true,
      progress: {
        stage: "complete",
        statusMessage: message,
        percent: 100,
      },
    }),

  failClustering: (error) =>
    set({
      isClustering: false,
      error,
      progress: { ...get().progress, stage: "error" },
    }),

  resetClustering: () =>
    set({
      isClustering: false,
      isCompleted: false,
      error: null,
      jobId: null,
      progress: { ...initialProgress },
    }),
}));
